import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { colors } from '../../brand-colors';

// IP Inventory categories
const IP_CATEGORIES = [
  {
    key: 'frameworks',
    label: 'Frameworks & Methodologies',
    description: 'Named systems, processes, or approaches you\'ve developed',
    placeholder: 'e.g., "The 5P Framework for...", "My signature methodology for..."'
  },
  {
    key: 'books',
    label: 'Books & Publications',
    description: 'Books, ebooks, guides, whitepapers you\'ve written',
    placeholder: 'e.g., "Author of [Book Title]", "Published guide on..."'
  },
  {
    key: 'courses',
    label: 'Courses & Training',
    description: 'Online courses, workshops, certifications you\'ve created',
    placeholder: 'e.g., "Creator of [Course Name]", "Training program on..."'
  },
  {
    key: 'processes',
    label: 'SOPs & Checklists',
    description: 'Documented procedures, templates, checklists',
    placeholder: 'e.g., "My client onboarding checklist", "Standard process for..."'
  },
  {
    key: 'presentations',
    label: 'Presentations & Talks',
    description: 'Signature presentations, keynotes, webinars',
    placeholder: 'e.g., "Keynote: [Title]", "My signature talk on..."'
  },
  {
    key: 'articles',
    label: 'Articles & Content',
    description: 'Blog series, video content, podcast topics',
    placeholder: 'e.g., "Weekly column on...", "Video series about..."'
  },
  {
    key: 'tools',
    label: 'Tools & Templates',
    description: 'Calculators, assessments, worksheets, templates',
    placeholder: 'e.g., "ROI Calculator", "Client assessment tool"'
  }
];

/**
 * IP Inventory - Standalone page
 * Catalog your intellectual property
 */
function IPInventory() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inventory, setInventory] = useState({});

  // Website scraping state
  const [scraping, setScraping] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);

  useEffect(() => {
    loadInventory();
  }, []);

  const loadInventory = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/authority-engineer/audit/inventory', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.inventory) {
          setInventory(data.inventory);
        }
      }
    } catch (err) {
      console.error('Error loading inventory:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInventoryChange = (key, value) => {
    setInventory(prev => ({ ...prev, [key]: value }));
  };

  const saveInventory = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/authority-engineer/audit/inventory', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inventory })
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Inventory saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to save inventory');
      }
    } catch (err) {
      setError('Failed to save inventory');
    } finally {
      setSaving(false);
    }
  };

  const scrapeWebsiteForIP = async () => {
    setScraping(true);
    setError('');

    try {
      const response = await fetch('/api/authority-engineer/audit/scrape-ip', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        setInventory(prev => ({
          ...prev,
          ...data.inventory
        }));
        setScrapedData(data);
      } else {
        setError(data.error || 'Failed to scan website');
      }
    } catch (err) {
      setError('Failed to scan website. Please try again.');
    } finally {
      setScraping(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4 px-4">
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading inventory...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-4">
      <div className="mb-4">
        <Link to="/command-center/authority-engineer" className="text-decoration-none mb-2 d-inline-block">
          ← Back to Authority Engineer
        </Link>
        <h1>IP Inventory</h1>
        <p className="text-muted">Catalog your intellectual property - frameworks, books, courses, and more</p>
      </div>

      <Row>
        <Col lg={10}>
          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
          {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

          {/* Scan Website Card */}
          <Card className="shadow mb-4" style={{ backgroundColor: '#f0fff4', border: '1px solid #9ae6b4' }}>
            <Card.Body className="p-4">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="mb-1" style={{ color: '#276749' }}>
                    <span className="me-2">Auto-Scan Your Website</span>
                  </h5>
                  <p className="text-muted mb-0">
                    We'll analyze your website and pre-populate your IP inventory automatically.
                  </p>
                </div>
                <Button
                  variant="success"
                  onClick={scrapeWebsiteForIP}
                  disabled={scraping}
                  style={{ minWidth: '160px' }}
                >
                  {scraping ? (
                    <><Spinner size="sm" className="me-2" />Scanning...</>
                  ) : (
                    'Scan My Website'
                  )}
                </Button>
              </div>
              {scrapedData && (
                <Alert variant="success" className="mt-3 mb-0">
                  <strong>Found IP on your website!</strong> We've pre-filled the form below.
                  {scrapedData.expertise_themes?.length > 0 && (
                    <div className="mt-2">
                      <strong>Expertise themes detected:</strong> {scrapedData.expertise_themes.join(', ')}
                    </div>
                  )}
                  {scrapedData.authority_signals?.length > 0 && (
                    <div className="mt-1">
                      <strong>Authority signals:</strong> {scrapedData.authority_signals.join(', ')}
                    </div>
                  )}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Inventory Form */}
          <Card className="shadow">
            <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
              <h5 className="mb-0">Your Intellectual Property</h5>
            </Card.Header>
            <Card.Body className="p-4">
              <p className="text-muted mb-4">
                {scrapedData
                  ? "Review what we found and add anything we missed. Edit any fields as needed."
                  : "Document everything you've created. This helps identify gaps and opportunities."}
              </p>

              {IP_CATEGORIES.map((category) => (
                <Form.Group key={category.key} className="mb-4">
                  <Form.Label>
                    <strong>{category.label}</strong>
                    <span className="text-muted ms-2 small">— {category.description}</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder={category.placeholder}
                    value={inventory[category.key] || ''}
                    onChange={(e) => handleInventoryChange(category.key, e.target.value)}
                  />
                </Form.Group>
              ))}

              <Form.Group className="mb-4">
                <Form.Label>
                  <strong>Other IP</strong>
                  <span className="text-muted ms-2 small">— Anything else that represents your expertise</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Awards, patents, certifications, notable client work..."
                  value={inventory.other || ''}
                  onChange={(e) => handleInventoryChange('other', e.target.value)}
                />
              </Form.Group>

              <div className="d-flex justify-content-between mt-4">
                <Link to="/command-center/authority-engineer" className="btn btn-outline-secondary">
                  ← Back
                </Link>
                <div>
                  <Button
                    variant="outline-dark"
                    className="me-2"
                    onClick={saveInventory}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Inventory'}
                  </Button>
                  <Link
                    to="/command-center/authority-engineer/gaps"
                    className="btn"
                    style={{ backgroundColor: colors.greyDarkest, border: 'none', color: 'white' }}
                  >
                    Generate Gap Analysis →
                  </Link>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default IPInventory;
